import React, { Component, Fragment } from "react";
import Breadcrumb from "../common/breadcrumb";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { connect } from "react-redux";
import { getSingleOrder, applyPaymentBatch } from "../../firebase/firebase.utils";

const api_key    = "dxglvephxsih8ownxsuap8apiokfxcqm";
const secret_key = "jz2yogdjkxza8gguqc7of4fr";
const base_url   = "https://portal.packzy.com/api/v1";

// Steadfast status → our DB orderStatus value
const SF_TO_ORDER_STATUS = {
  "delivered":         "Delivered",
  "partial_delivered": "Partial Delivered",
};

// Steadfast status → badge colors
function statusBadge(status) {
  if (status === "delivered") {
    return (
      <span style={{ background: "#F0FDF4", color: "#166534", borderRadius: 5, padding: "2px 10px", fontWeight: 700, fontSize: 12 }}>
        {status}
      </span>
    );
  }
  if (status === "partial_delivered") {
    return (
      <span style={{ background: "#FEF9C3", color: "#854d0e", borderRadius: 5, padding: "2px 10px", fontWeight: 700, fontSize: 12 }}>
        {status}
      </span>
    );
  }
  return (
    <span style={{ background: "#EEF2FF", color: "#3730a3", borderRadius: 5, padding: "2px 10px", fontWeight: 600, fontSize: 12 }}>
      {status ?? "—"}
    </span>
  );
}

const hdStyle = {
  padding: "8px 12px",
  textAlign: "left",
  color: "#64748b",
  fontWeight: 600,
  borderBottom: "1px solid #e2e8f0",
  whiteSpace: "nowrap",
};
const tdStyle = { padding: "8px 12px", whiteSpace: "nowrap" };
const btnBase = {
  borderRadius: 7,
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
  border: "none",
  padding: "6px 16px",
};

class SteadfastPaymentDetail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      payment: null,
      error: null,
      receivePreview: null,
      receiveLoading: false,
      receiveConfirming: false,
      processed: false,
    };
  }

  componentDidMount() {
    const paymentId = this.props.match?.params?.id;
    if (paymentId) this.fetchPayment(paymentId);
  }

  getMonthName = () => {
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return months[new Date().getMonth()];
  };

  fetchPayment = async (paymentId) => {
    this.setState({ loading: true, error: null });
    try {
      const res = await axios.get(`${base_url}/payments/${paymentId}`, {
        headers: { "Api-Key": api_key, "Secret-Key": secret_key, "Content-Type": "application/json" },
      });
      this.setState({ payment: res.data.payment, loading: false });
    } catch (e) {
      this.setState({
        error: "Failed to load payment: " + (e.response?.data?.message || e.message),
        loading: false,
      });
    }
  };

  handleReceivePayment = async () => {
    const { payment } = this.state;
    const consignments = payment.consignments || [];
    this.setState({ receiveLoading: true, receivePreview: null });

    const preview = await Promise.all(consignments.map(async (c) => {
      try {
        const orderData = await getSingleOrder(String(c.invoice));
        if (!orderData) return { consignment: c, notFound: true };

        const order = { id: String(c.invoice), ...orderData };
        const totalBill   = Number(order.totalBill || 0);
        const alreadyPaid = (order.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);
        const totalDue    = totalBill - alreadyPaid;

        // This specific consignment was already recorded → skip
        const alreadyApplied = (order.payments || []).some(
          (p) => String(p.transactionId) === String(c.consignment_id)
        );

        // Order already fully settled → skip
        const alreadyFullyPaid = totalDue <= 0;

        // Cap COD at what's actually owed — never overpay
        const appliedAmount = Math.max(0, Math.min(Number(c.cod_amount || 0), totalDue));

        const newTotalPaid   = alreadyPaid + appliedAmount;
        const paymentStatus  = newTotalPaid >= totalBill ? "Paid" : "Partially Paid";

        // Map Steadfast delivery status to our DB orderStatus
        const sfOrderStatus = SF_TO_ORDER_STATUS[c.status] || null;

        return {
          consignment: c,
          order,
          totalBill,
          alreadyPaid,
          totalDue,
          appliedAmount,
          paymentStatus,
          sfOrderStatus,
          alreadyApplied,
          alreadyFullyPaid,
        };
      } catch {
        return { consignment: c, notFound: true };
      }
    }));

    this.setState({ receivePreview: preview, receiveLoading: false });
  };

  handleConfirmReceive = async () => {
    const { payment, receivePreview } = this.state;
    const adminName = (this.props.currentAdmin && this.props.currentAdmin.name) || "";
    const date = new Date();

    // Skip: not found, already applied to this consignment ID, or order already fully paid
    const applicable = receivePreview.filter(
      (i) => !i.notFound && !i.alreadyApplied && !i.alreadyFullyPaid && i.appliedAmount > 0
    );

    if (applicable.length === 0) {
      toast.error("No applicable consignments to process.");
      return;
    }

    this.setState({ receiveConfirming: true });

    const ordersToUpdate = applicable.map(({ consignment: c, order, paymentStatus, appliedAmount, sfOrderStatus }) => {
      const newPaymentEntry = {
        amount: appliedAmount,
        method: "SteadFast (COD)",
        receivedBy: adminName,
        date: date.toLocaleDateString("en-GB"),
        transactionId: String(c.consignment_id),
      };
      const updatedOrder = {
        ...order,
        paymentStatus,
        payments: [...(order.payments || []), newPaymentEntry],
      };
      // Only update orderStatus when Steadfast confirms delivery/partial delivery
      if (sfOrderStatus) updatedOrder.orderStatus = sfOrderStatus;
      return { updatedOrder };
    });

    // Deterministic ids (not Date.now()-based) so re-confirming the same
    // Steadfast payment — e.g. after a page refresh, since `processed` is
    // just in-memory state — overwrites the same cash-in/expense docs
    // instead of creating duplicates. Per-order payments are already
    // protected via the alreadyApplied/alreadyFullyPaid checks above,
    // re-derived from live Firestore state on every preview; this closes
    // the same gap for the cash-in and cash-out records themselves.
    const cashIns = applicable.map(({ consignment: c, order, appliedAmount }) => ({
      id: `steadfast_${c.consignment_id}`,
      category: "ORDERS",
      subCategory: "SteadFast (COD)",
      month: this.getMonthName(),
      date: date.toLocaleDateString("en-GB"),
      note: `Order Id:${order.id}`,
      amount: appliedAmount,
      receiveBy: adminName,
      status: "pending",
      unEditable: true,
    }));

    const cashOutAmount = Number(payment.charges || 0) + Number(payment.due_bills || 0);
    const expense = {
      id: `steadfast_payment_${payment.payment_id}`,
      category: "Delivery charge",
      subCategory: String(payment.payment_id),
      month: this.getMonthName(),
      date: date.toLocaleDateString("en-GB"),
      note: `Steadfast Payment #${payment.payment_id} | ${applicable.length} consignments | Total: ৳${payment.total} | Charges: ৳${payment.charges} | Due Bills: ${payment.due_bills}`,
      amount: cashOutAmount,
      receiveBy: adminName,
      status: "pending",
      unEditable: true,
    };

    try {
      await applyPaymentBatch({ ordersToUpdate, cashIns, expense });
      this.setState({ processed: true, receivePreview: null, receiveConfirming: false });
      toast.success(`Applied for ${applicable.length} consignment(s). Cash-out: ৳${cashOutAmount}.`);
    } catch (e) {
      toast.error("Failed — nothing was saved. " + (e.message || String(e)));
      this.setState({ receiveConfirming: false });
    }
  };

  render() {
    const { loading, payment, error, receivePreview, receiveLoading, receiveConfirming, processed } = this.state;
    const paymentId = this.props.match?.params?.id;

    return (
      <Fragment>
      <Breadcrumb title={`Payment ${paymentId}`} parent="Steadfast" />
      <div className="container-fluid">
        <div className="card" style={{ padding: "20px 24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#1e293b" }}>Steadfast Payment</span>
          <span style={{ fontFamily: "monospace", fontSize: 13, color: "#6366f1", background: "#eef2ff", borderRadius: 6, padding: "3px 10px" }}>{paymentId}</span>
        </div>

        {loading && (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8", fontSize: 13 }}>
            Loading payment details…
          </div>
        )}

        {error && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "20px 24px", color: "#991b1b", fontWeight: 600 }}>
            {error}
          </div>
        )}

        {!loading && payment && (() => {
          const consignments = payment.consignments || [];
          return (
            <div>

              {/* Summary pills */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20, alignItems: "center" }}>
                <span style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>{payment.payment_id}</span>
                <span style={{ background: payment.status_label === "paid" ? "#F0FDF4" : "#FEF9C3", color: payment.status_label === "paid" ? "#166534" : "#854d0e", borderRadius: 5, padding: "3px 12px", fontSize: 13, fontWeight: 700 }}>{payment.status_label}</span>
                <span style={{ fontSize: 13, color: "#64748b" }}>{payment.method}</span>
                {[
                  { label: "Amount",     value: `৳ ${payment.amount}`,   color: "#059669" },
                  { label: "Total",      value: `৳ ${payment.total}`,    color: "#1e293b" },
                  { label: "Charges",    value: `৳ ${payment.charges}`,  color: "#dc2626" },
                  { label: "Paid Bills", value: payment.paid_bills,       color: "#1e293b" },
                  { label: "Due Bills",  value: payment.due_bills,        color: payment.due_bills > 0 ? "#dc2626" : "#1e293b" },
                ].map(({ label, value, color }) => (
                  <span key={label} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 6, padding: "4px 12px", fontSize: 13 }}>
                    <span style={{ color: "#94a3b8" }}>{label}: </span>
                    <span style={{ fontWeight: 700, color }}>{value}</span>
                  </span>
                ))}
                <span style={{ fontSize: 12, color: "#94a3b8" }}>Created: {payment.created_at}</span>
                <span style={{ fontSize: 12, color: "#94a3b8" }}>Paid: {payment.paid_at}</span>
              </div>

              {/* Receive Payment */}
              {consignments.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  {processed ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#F0FDF4", color: "#166534", border: "1px solid #bbf7d0", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 700 }}>
                      ✓ Payment Received
                    </span>
                  ) : receivePreview ? (
                    <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: "16px 18px" }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", marginBottom: 10 }}>Confirm Payment Application</div>
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: "#f8fafc" }}>
                              {["Invoice", "COD Amount", "Already Paid", "Due", "Will Apply", "Payment Status", "Order Status", "Note"].map((h) => (
                                <th key={h} style={hdStyle}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {receivePreview.map((item, ri) => {
                              const skip = item.notFound || item.alreadyApplied || item.alreadyFullyPaid;
                              return (
                                <tr key={ri} style={{ borderBottom: "1px solid #f1f5f9", opacity: skip ? 0.45 : 1 }}>
                                  <td style={{ ...tdStyle, fontWeight: 600, color: "#4f46e5" }}>{item.consignment.invoice ?? "—"}</td>
                                  <td style={{ ...tdStyle, color: "#64748b" }}>৳ {item.consignment.cod_amount ?? "—"}</td>
                                  <td style={tdStyle}>{item.notFound ? "—" : `৳ ${item.alreadyPaid}`}</td>
                                  <td style={{ ...tdStyle, fontWeight: 600 }}>{item.notFound ? "—" : `৳ ${item.totalDue}`}</td>
                                  <td style={{ ...tdStyle, fontWeight: 700, color: skip ? "#94a3b8" : "#059669" }}>
                                    {item.notFound ? "—" : skip ? "—" : `৳ ${item.appliedAmount}`}
                                    {!item.notFound && !skip && item.appliedAmount < Number(item.consignment.cod_amount) && (
                                      <span style={{ marginLeft: 4, fontSize: 10, color: "#f59e0b", fontWeight: 600 }}>(capped)</span>
                                    )}
                                  </td>
                                  <td style={tdStyle}>
                                    {item.notFound ? (
                                      <span style={{ color: "#94a3b8" }}>—</span>
                                    ) : item.alreadyApplied ? (
                                      <span style={{ background: "#FEF9C3", color: "#854d0e", borderRadius: 5, padding: "2px 8px", fontWeight: 600 }}>Already applied</span>
                                    ) : item.alreadyFullyPaid ? (
                                      <span style={{ background: "#F0FDF4", color: "#166534", borderRadius: 5, padding: "2px 8px", fontWeight: 600 }}>Already Paid</span>
                                    ) : (
                                      <span style={{ background: item.paymentStatus === "Paid" ? "#F0FDF4" : "#EEF2FF", color: item.paymentStatus === "Paid" ? "#166534" : "#3730a3", borderRadius: 5, padding: "2px 8px", fontWeight: 600 }}>
                                        {item.paymentStatus}
                                      </span>
                                    )}
                                  </td>
                                  <td style={tdStyle}>
                                    {item.sfOrderStatus ? (
                                      <span style={{ background: item.sfOrderStatus === "Delivered" ? "#F0FDF4" : "#FEF9C3", color: item.sfOrderStatus === "Delivered" ? "#166534" : "#854d0e", borderRadius: 5, padding: "2px 8px", fontWeight: 600 }}>
                                        → {item.sfOrderStatus}
                                      </span>
                                    ) : <span style={{ color: "#94a3b8" }}>—</span>}
                                  </td>
                                  <td style={{ ...tdStyle, color: "#94a3b8" }}>
                                    {item.notFound ? "Order not found"
                                      : item.alreadyApplied ? "Skipped (consignment already recorded)"
                                      : item.alreadyFullyPaid ? "Skipped (order already fully paid)"
                                      : "Will apply SteadFast (COD)"}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ marginTop: 12, padding: "8px 12px", background: "#F0FDF4", borderRadius: 7, fontSize: 12, color: "#166534", fontWeight: 600 }}>
                        Cash-in total: ৳{receivePreview
                          .filter((i) => !i.notFound && !i.alreadyApplied && !i.alreadyFullyPaid)
                          .reduce((s, i) => s + (i.appliedAmount || 0), 0)}
                        {" "}({receivePreview.filter((i) => !i.notFound && !i.alreadyApplied && !i.alreadyFullyPaid && i.appliedAmount > 0).length} consignment(s) to apply)
                      </div>
                      <div style={{ marginTop: 8, padding: "8px 12px", background: "#fef2f2", borderRadius: 7, fontSize: 12, color: "#991b1b", fontWeight: 600 }}>
                        Cash-out entry: ৳{Number(payment.charges || 0) + Number(payment.due_bills || 0)} (Charges ৳{payment.charges} + Due Bills {payment.due_bills})
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                        <button onClick={this.handleConfirmReceive} disabled={receiveConfirming} style={{ ...btnBase, background: receiveConfirming ? "#d1fae5" : "#059669", color: "white", cursor: receiveConfirming ? "not-allowed" : "pointer" }}>
                          {receiveConfirming ? "Applying…" : "Confirm & Apply"}
                        </button>
                        <button onClick={() => this.setState({ receivePreview: null })} disabled={receiveConfirming} style={{ ...btnBase, background: "#f1f5f9", color: "#475569", cursor: "pointer" }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={this.handleReceivePayment} disabled={receiveLoading} style={{ ...btnBase, background: "#00B795", color: "white", cursor: receiveLoading ? "not-allowed" : "pointer", opacity: receiveLoading ? 0.7 : 1 }}>
                      {receiveLoading ? "Looking up orders…" : "Receive Payment"}
                    </button>
                  )}
                </div>
              )}

              {/* Consignments table */}
              {consignments.length > 0 ? (
                <div style={{ overflowX: "auto" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>
                    Consignments ({consignments.length})
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: "#f1f5f9" }}>
                        {["Consignment ID", "Invoice", "Tracking", "Recipient", "COD Amount", "Status"].map((h) => (
                          <th key={h} style={hdStyle}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {consignments.map((c, ci) => (
                        <tr key={ci} style={{ borderBottom: "1px solid #e2e8f0" }}>
                          <td style={{ ...tdStyle, fontWeight: 600 }}>{c.consignment_id ?? "—"}</td>
                          <td style={tdStyle}>{c.invoice ?? "—"}</td>
                          <td style={{ ...tdStyle, fontFamily: "monospace" }}>{c.tracking_code ?? "—"}</td>
                          <td style={tdStyle}>
                            <div style={{ fontWeight: 600 }}>{c.recipient_name ?? "—"}</div>
                            <div style={{ color: "#94a3b8", fontSize: 11 }}>{c.recipient_phone ?? ""}</div>
                          </td>
                          <td style={{ ...tdStyle, fontWeight: 700, color: "#059669" }}>৳ {c.cod_amount ?? "—"}</td>
                          <td style={tdStyle}>{statusBadge(c.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ color: "#94a3b8", fontSize: 13 }}>No consignments found.</div>
              )}
            </div>
          );
        })()}

        <ToastContainer />
        </div>
      </div>
      </Fragment>
    );
  }
}

const mapStateToProps = (state) => ({ currentAdmin: state.admins.currentAdmin });
export default connect(mapStateToProps)(SteadfastPaymentDetail);
